
import React from 'react';
import { AlertCircle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SubscriptionFeatures } from '@/lib/types';
import { usePlanSelectDialog } from '@/contexts/PlanSelectDialogContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TranslationEditButton } from '@/components/i18n/TranslationEditButton';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { useTranslation } from 'react-i18next';

interface TranslationLink {
  key: string;
  text: string;
}

interface FeaturePreviewProps {
  feature: keyof SubscriptionFeatures;
  title: string;
  description: string;
  previewContent?: React.ReactNode;
  translationLinks?: TranslationLink[];
  compact?: boolean;
}

export function FeaturePreview({
  feature,
  title,
  description,
  previewContent,
  translationLinks = [],
  compact = false
}: FeaturePreviewProps) {
  const { openDialog } = usePlanSelectDialog();
  const { t } = useTranslation();

  if (compact) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3 relative my-2">
        <div className="flex items-start gap-2">
          <div className="flex-shrink-0 mt-0.5">
            <AlertCircle className="h-4 w-4 text-amber-500" />
          </div>
          <div className="flex-grow">
            <div className="flex items-center gap-1 mb-1">
              <h4 className="text-sm font-medium">{title}</h4>
              <HoverCard>
                <HoverCardTrigger>
                  <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </HoverCardTrigger>
                <HoverCardContent className="w-80">
                  <p className="text-sm text-muted-foreground">{description}</p>
                </HoverCardContent>
              </HoverCard>
              {translationLinks.length > 0 && translationLinks.map((link, index) => (
                <TranslationEditButton 
                  key={index}
                  translationKey={link.key} 
                  tooltipText={link.text}
                  size="sm"
                  variant="ghost"
                  className="h-5 p-0"
                />
              ))}
            </div>
            <Button 
              size="sm" 
              onClick={openDialog}
              className="w-full text-xs py-1 h-7"
            >
              {t('upgradeToAccess', 'Upgrade to Access')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="pointer-events-none opacity-60">
        {previewContent}
      </div>
      
      <div className="absolute inset-0 bg-background/50 backdrop-blur-[1px] flex items-center justify-center">
        <div className="bg-card p-4 rounded-lg shadow-lg max-w-sm mx-auto text-center space-y-3">
          <div className="flex items-center justify-center gap-2 text-primary">
            <AlertCircle className="h-5 w-5" />
            <h4 className="font-medium">{title}</h4>
            {translationLinks.length > 0 && translationLinks.map((link, index) => (
              <TranslationEditButton 
                key={index}
                translationKey={link.key} 
                tooltipText={link.text}
              />
            ))}
          </div>
          <p className="text-sm text-muted-foreground">{description}</p>
          <Button 
            size="sm" 
            onClick={openDialog}
            className="w-full"
          >
            {t('upgradeToAccess', 'Upgrade to Access')}
          </Button>
        </div>
      </div>
    </div>
  );
}
